import type { ReviewCommentRecord } from '@mesa/core/review';
import { CheckIcon, XIcon } from 'lucide-react';
import { memo } from 'react';
import { toast } from 'sonner';

import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCommentResolution } from '@/features/comments/hooks/use-comment-resolution';


interface CommentResolutionHoverButtonProps {
  comment: ReviewCommentRecord;
}

export const CommentResolutionHoverButton = memo(({ comment }: CommentResolutionHoverButtonProps) => {
  const { resolveComment, unresolveComment } = useCommentResolution();
  const isResolved = comment.resolvedById !== null;

  const handleToggleResolution = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!comment.id) {
      toast.error('Cannot resolve comment: missing comment ID');
      return;
    }

    try {
      if (isResolved) {
        await unresolveComment(comment.id);
      } else {
        await resolveComment(comment.id);
      }
    } catch (error) {
      console.error('Error toggling comment resolution:', error);
      toast.error(isResolved ? 'Failed to unresolve comment thread' : 'Failed to resolve comment thread');
    }
  };

  return (
    <div
      className="absolute -top-2 right-9 z-10 w-7 h-6 rounded-sm flex items-center justify-center bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-250 cursor-pointer"
      onClick={handleToggleResolution}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {isResolved ? (
              <XIcon className="size-5 opacity-80" strokeWidth={1.5} />
            ) : (
              <CheckIcon className="size-5 opacity-80" strokeWidth={1.5} />
            )}
          </TooltipTrigger>
          <TooltipContent>{isResolved ? 'Unresolve conversation' : 'Resolve conversation'}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
});
